import Image, { type ImageProps } from '2-core-elements/Image/Image';
import Chip from '3-components/Chip/Chip';
import SearchItemMetadata from './SearchItemMetadata';
import DownloadCard from '4-sections/DownloadList/DownloadCard';
import ExternalResourcesCard from '4-sections/ExternalResourcesCard/ExternalResourcesCard';
import { SEARCH_RESULTS_TYPES } from './SearchBlock';

export interface SearchItemTagProps {
    label: string;
}

export type SearchItemProps = {
    img?: Pick<ImageProps, 'imgSrc' | 'imgAlt' | 'imgHeight' | 'imgWidth'> | string;
    link?: { href: string; label?: string; target?: '_self' | '_blank' } | string;
    title: string;
    subtitle?: string;
    tags?: SearchItemTagProps[] | string;
    children?: React.ReactNode;
    eventLocation?: string;
    eventStartDate?: string;
    eventEndDate?: string;
    newsPublishDate?: string;
    fileType?: string;
    fileSize?: string;
    documentFileViewURL?: string;
    documentFileDownloadURL?: string;
    type?: string;
};

const SearchItem = ({
    img,
    link = { href: '', label: '', target: '_self' },
    title,
    subtitle,
    tags,
    children,
    eventLocation,
    eventStartDate,
    eventEndDate,
    newsPublishDate,
    fileType,
    fileSize,
    documentFileViewURL,
    documentFileDownloadURL,
    type,
}: SearchItemProps): JSX.Element => {
    const imgSrc = typeof img === 'string' ? img : img?.imgSrc ?? '';
    const imgAlt = typeof img === 'string' ? '' : img?.imgAlt;

    const renderDocumentCard = (): JSX.Element => {
        return (
            <DownloadCard
                title={title}
                fileType={fileType}
                fileSize={fileSize}
                documentFileViewURL={documentFileViewURL}
                documentFileDownloadURL={documentFileDownloadURL}
                tags={tags as string}
            >
                {children}
            </DownloadCard>
        );
    };

    const renderExternalLinksCard = (): JSX.Element => {
        return (
            <ExternalResourcesCard
                title={title}
                url={typeof link === 'string' ? link : link.href}
                tags={tags as string}
            >
                {children}
            </ExternalResourcesCard>
        );
    };

    const renderDefaultCard = (): JSX.Element => {
        return (
            <a href={typeof link === 'string' ? link : link.href} className={`search-item${img ? ' search-item-with-img' : ''}`} aria-label={title}>
                <div className="item-content">
                    <div>
                        <h3 className="item-title">{title}</h3>
                        {subtitle && <p className="item-subtitle">{subtitle}</p>}
                        {(eventStartDate || eventEndDate || eventLocation || newsPublishDate) && (
                            <SearchItemMetadata
                                eventStartDate={eventStartDate}
                                eventEndDate={eventEndDate}
                                eventLocation={eventLocation}
                                newsPublishDate={newsPublishDate}
                            />
                        )}
                        {children && <div className="item-description">{children}</div>}
                    </div>
                    {tags && typeof tags === 'string' && (
                        <Chip
                            skin={tags === 'Safety alert' ? 'error' : 'primary'}
                            variant={tags === 'General pages' ? 'subtle' : 'bold'}
                            label={tags}
                        />
                    )}
                    {Array.isArray(tags) && tags.length > 0 && (
                        <div className="item-tags">
                            {tags.map(props => {
                                return (
                                    <div key={props.label}>
                                        <Chip skin="primary" variant="bold" label={props.label} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                {img && (
                    <div className="item-img">
                        <Image imgSrc={imgSrc} imgAlt={imgAlt} />
                    </div>
                )}
            </a>
        );
    };

    return type === SEARCH_RESULTS_TYPES.DOCUMENTS
        ? renderDocumentCard()
        : type === SEARCH_RESULTS_TYPES.EXTERNAL_LINKS
            ? renderExternalLinksCard()
            : renderDefaultCard();
};

export default SearchItem;
